import { startAuthentication } from '@simplewebauthn/browser';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';

/**
 * ログイン。二段階認証が有効なら、コード入力に切り替わる。
 * セッションは HttpOnly Cookie で返るので、画面側でトークンを保持しない。
 */
export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<'password' | 'mfa'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (step === 'password') {
        const result = await api.post<{ mfaRequired: boolean }>('/api/auth/login', {
          email,
          password,
        });
        if (result.mfaRequired) setStep('mfa');
        else onSignedIn();
      } else {
        await api.post('/api/auth/mfa/verify', { code });
        onSignedIn();
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Passkey でログインする。
   *
   * どのメールアドレスが登録済みかを未認証の相手に漏らさないため、
   * サーバーは allowCredentials を返さない (discoverable credential 前提)。
   * ブラウザ側でも先にメールアドレスを尋ねない。
   */
  async function signInWithPasskey() {
    setBusy(true);
    setError(null);
    try {
      const start = await api.post<{ challengeId: string; options: unknown }>(
        '/api/auth/passkey/authentication/options',
      );
      const response = await startAuthentication({
        optionsJSON: start.options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      await api.post('/api/auth/passkey/authentication/verify', {
        challengeId: start.challengeId,
        response,
      });
      onSignedIn();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Passkey でのログインを中止しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas px-5 sm:px-6">
      {/*
        ログインは LP からの行き止まりになりやすいので、戻る導線を先頭に置く。
        カード内のロゴはブランドの表示に徹させ、ナビゲーションはここ 1 箇所にする。
      */}
      <div className="py-6">
        <Link to="/" className="text-sm text-ink-muted transition-colors hover:text-ink">
          トップへ戻る
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-center pb-12 sm:pb-20">
        <form
          onSubmit={submit}
          className="flex w-full max-w-96 flex-col gap-6 border border-line bg-surface-raised px-5 py-8 sm:px-8 sm:py-10"
        >
          <Logo height={48} className="self-center text-brand-500" />

          {step === 'password' ? (
            <>
              <label className="flex flex-col gap-2 text-sm">
                メールアドレス
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border border-line-bold bg-surface-strong px-3 py-2.5 text-sm focus:outline-2 focus:outline-offset-0 focus:outline-brand-700"
                />
              </label>
              <label className="flex flex-col gap-2 text-sm">
                パスワード
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border border-line-bold bg-surface-strong px-3 py-2.5 text-sm focus:outline-2 focus:outline-offset-0 focus:outline-brand-700"
                />
              </label>
            </>
          ) : (
            <label className="flex flex-col gap-2 text-sm">
              認証コード
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="border border-line-bold bg-surface-strong px-3 py-2.5 font-mono text-sm focus:outline-2 focus:outline-offset-0 focus:outline-brand-700"
              />
              <span className="text-xs text-ink-subtle">
                認証アプリのコード、またはリカバリーコードを入力してください。
              </span>
            </label>
          )}

          {error ? <p className="text-sm text-alert">{error}</p> : null}

          <Button type="submit" variant="primary" size="md" disabled={busy}>
            {busy ? '確認中…' : step === 'password' ? 'ログイン' : '認証する'}
          </Button>

          {step === 'password' ? (
            <Button type="button" size="md" disabled={busy} onClick={signInWithPasskey}>
              Passkey でログイン
            </Button>
          ) : null}
        </form>
      </div>
    </div>
  );
}
